import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Handshake, ArrowLeft, ArrowRight, Plus, MapPin, Send,
  CheckCircle, MessageCircle, Filter, Search, ChevronDown, HelpCircle
} from 'lucide-react';
import axios from 'axios';
import { Joyride } from 'react-joyride';
import { useToast } from '../contexts/ToastContext';
import {
  isAuthenticated,
  getAuthHeader,
  getCertifiedNetworkStatus
} from '../services/otpAuthService';
import type { MemberLocation } from '../components/MapWrapper';

const MapWrapper = lazy(() => import('../components/MapWrapper'));

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const PRACTICE_TYPES = [
  'Immatricolazioni e passaggi di proprietà',
  'Consulenza per le pratiche agricole e operatrici',
  'Trasporto merci',
  'Nazionalizzazioni',
  'Consulenze tecniche',
  'Trasporti eccezionali',
  'Servizi nautici (Studio abilitato STED)',
  'Trasporto rifiuti',
  'Rinnovo patenti (comprese le patenti nautiche)'
];

const PRACTICE_ICONS: Record<string, string> = {
  'Immatricolazioni e passaggi di proprietà': '🚗',
  'Consulenza per le pratiche agricole e operatrici': '🚜',
  'Trasporto merci': '🚛',
  'Nazionalizzazioni': '🌍',
  'Consulenze tecniche': '🔧',
  'Trasporti eccezionali': '🚚',
  'Servizi nautici (Studio abilitato STED)': '⛵',
  'Trasporto rifiuti': '♻️',
  'Rinnovo patenti (comprese le patenti nautiche)': '🪪'
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  'new': { label: 'Nuovo', color: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  'in-progress': { label: 'In Corso', color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  'resolved': { label: 'Risolto', color: 'text-green-700 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/30' },
  'closed': { label: 'Chiuso', color: 'text-gray-700 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-900/30' }
};

interface Collaboration {
  _id: string;
  requesterId: string;
  requesterSnapshot: { nominativo: string; citta: string; provincia: string; email: string };
  targetId: string;
  targetSnapshot: { nominativo: string; citta: string; provincia: string; email: string };
  practiceType: string;
  status: string;
  conversation: { sender: string; senderName: string; message: string; sentAt: string; readAt?: string }[];
  unreadCount?: number;
  createdAt: string;
  updatedAt: string;
}

export default function CollaboraPage() {
  const navigate = useNavigate();
  const toast = useToast();

  // Auth
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedPractice, setSelectedPractice] = useState('');
  const [mapMembers, setMapMembers] = useState<(MemberLocation & { associateId?: string })[]>([]);
  const [selectedMember, setSelectedMember] = useState<(MemberLocation & { associateId?: string }) | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [createdId, setCreatedId] = useState('');

  // Map filters
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [searchName, setSearchName] = useState('');

  // Map settings
  const [mapSettings, setMapSettings] = useState<any>(null);

  // List state
  const [tab, setTab] = useState<'sent' | 'received'>('received');
  const [collaborations, setCollaborations] = useState<Collaboration[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');

  // Guided tours (uno per contesto)
  const TOUR_LIST_KEY = 'collabora_tour_list';
  const TOUR_MAP_KEY = 'collabora_tour_map';
  const TOUR_MSG_KEY = 'collabora_tour_msg';
  const [runTourList, setRunTourList] = useState(false);
  const [runTourMap, setRunTourMap] = useState(false);
  const [runTourMsg, setRunTourMsg] = useState(false);
  const [tourListKey, setTourListKey] = useState(0);
  const [tourMapKey, setTourMapKey] = useState(0);
  const [tourMsgKey, setTourMsgKey] = useState(0);

  const listTourSteps = [
    {
      target: '#tour-btn-nuova',
      content: 'Clicca qui per creare una nuova richiesta di collaborazione con un collega della Rete Certificata.',
      disableBeacon: true,
      placement: 'bottom' as const
    },
    {
      target: '#tour-tabs',
      content: 'Qui puoi passare tra le collaborazioni ricevute dai colleghi e quelle inviate da te.',
      placement: 'bottom' as const
    },
    {
      target: '#tour-filters',
      content: 'Usa questi filtri per trovare rapidamente le collaborazioni per stato: nuove, in corso o risolte.',
      placement: 'bottom' as const
    },
    {
      target: '#tour-list',
      content: 'Le tue collaborazioni appariranno in questa lista. Clicca su una per aprire la conversazione.',
      placement: 'top' as const
    }
  ];

  const mapTourSteps = [
    {
      target: '#tour-map-filters',
      content: 'Usa i filtri per restringere la ricerca: cerca per nome, regione, provincia o città.',
      disableBeacon: true,
      placement: 'bottom' as const
    },
    {
      target: '#tour-map-container',
      content: 'Clicca su un marcatore nella mappa per vedere i dettagli dello studio e selezionarlo.',
      placement: 'top' as const
    },
    {
      target: '#tour-map-list',
      content: 'Oppure scorri questa lista e clicca sullo studio che preferisci per selezionarlo direttamente.',
      placement: 'top' as const
    }
  ];

  const handleListTourEvent = (data: any) => {
    if (data.type === 'tour:end' || data.action === 'skip' || data.action === 'close') {
      setRunTourList(false);
      localStorage.setItem(TOUR_LIST_KEY, 'true');
    }
  };

  const handleMapTourEvent = (data: any) => {
    if (data.type === 'tour:end' || data.action === 'skip' || data.action === 'close') {
      setRunTourMap(false);
      localStorage.setItem(TOUR_MAP_KEY, 'true');
    }
  };

  const msgTourSteps = [
    {
      target: '#tour-msg-info',
      content: 'Verifica che il collega e il tipo di pratica siano corretti.',
      disableBeacon: true,
      placement: 'bottom' as const
    },
    {
      target: '#tour-msg-textarea',
      content: 'Descrivi qui la tua richiesta: spiega di cosa hai bisogno e il tipo di collaborazione che cerchi.',
      placement: 'top' as const
    },
    {
      target: '#tour-msg-send',
      content: 'Quando sei pronto, clicca qui per inviare la richiesta. Il collega riceverà una notifica.',
      placement: 'top' as const
    }
  ];

  const handleMsgTourEvent = (data: any) => {
    if (data.type === 'tour:end' || data.action === 'skip' || data.action === 'close') {
      setRunTourMsg(false);
      localStorage.setItem(TOUR_MSG_KEY, 'true');
    }
  };

  // Auth check
  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthenticated()) {
        navigate('/accesso-associati');
        return;
      }
      try {
        const status = await getCertifiedNetworkStatus();
        if (!status?.isMember || !status?.isActive) {
          toast.error('Accesso riservato ai membri attivi della Rete Certificata');
          navigate('/area-riservata');
          return;
        }
        setAuthorized(true);
      } catch {
        navigate('/accesso-associati');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate, toast]);

  // Load collaborations list
  const loadCollaborations = useCallback(async (page = 1) => {
    setListLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: '10', tab };
      if (statusFilter) params.status = statusFilter;

      const { data } = await axios.get(`${API_BASE}/collaborations/my`, {
        headers: getAuthHeader(),
        params
      });

      if (data.success) {
        setCollaborations(data.data);
        setPagination(data.pagination);
      }
    } catch {
      toast.error('Errore nel caricamento delle collaborazioni');
    } finally {
      setListLoading(false);
    }
  }, [tab, statusFilter, toast]);

  useEffect(() => {
    if (authorized && !wizardOpen) {
      loadCollaborations();
      // Avvia tour lista alla prima visita
      if (!localStorage.getItem(TOUR_LIST_KEY)) {
        setTimeout(() => setRunTourList(true), 600);
      }
    }
  }, [authorized, wizardOpen, loadCollaborations]);

  // Load map data
  const loadMapData = useCallback(async (practiceType: string) => {
    setMapLoading(true);
    try {
      // Load map settings
      if (!mapSettings) {
        try {
          const settingsRes = await axios.get(`${API_BASE.replace('/api', '')}/api/public/settings/map`);
          if (settingsRes.data?.success) setMapSettings(settingsRes.data.data);
        } catch { /* use defaults */ }
      }

      const { data } = await axios.get(`${API_BASE}/collaborations/map`, {
        headers: getAuthHeader(),
        params: { practiceType }
      });

      if (data.success) {
        setMapMembers(data.data.members);
      }
    } catch {
      toast.error('Errore nel caricamento della mappa');
    } finally {
      setMapLoading(false);
    }
  }, [toast, mapSettings]);

  // Wizard handlers
  const handlePracticeSelect = (practice: string) => {
    setSelectedPractice(practice);
    setSelectedMember(null);
    setSelectedRegion('');
    setSelectedProvince('');
    setSelectedCity('');
    setSearchName('');
    setStep(2);
    loadMapData(practice);
    // Tour mappa alla prima volta
    if (!localStorage.getItem(TOUR_MAP_KEY)) {
      setTimeout(() => setRunTourMap(true), 800);
    }
  };

  const handleMemberSelect = (member: MemberLocation & { associateId?: string }) => {
    setSelectedMember(member);
    setStep(3);
    if (!localStorage.getItem(TOUR_MSG_KEY)) {
      setTimeout(() => setRunTourMsg(true), 500);
    }
  };

  const handleSubmit = async () => {
    if (!selectedMember?.associateId || !message.trim()) return;

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/collaborations`, {
        targetAssociateId: selectedMember.associateId,
        practiceType: selectedPractice,
        message: message.trim()
      }, { headers: getAuthHeader() });

      if (data.success) {
        setCreatedId(data.data._id);
        setStep(4);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Errore durante l\'invio della richiesta');
    } finally {
      setSubmitting(false);
    }
  };

  const resetWizard = () => {
    setWizardOpen(false);
    setStep(1);
    setSelectedPractice('');
    setSelectedMember(null);
    setMessage('');
    setMapMembers([]);
    setCreatedId('');
    setSelectedRegion('');
    setSelectedProvince('');
    setSelectedCity('');
    setSearchName('');
  };

  // Map filters - computed
  const uniqueRegions = Array.from(new Set(mapMembers.map(m => m.region))).filter(Boolean).sort();
  const regionFilteredMembers = selectedRegion ? mapMembers.filter(m => m.region === selectedRegion) : mapMembers;
  const uniqueProvinces = Array.from(new Set(regionFilteredMembers.map(m => m.province))).filter(Boolean).sort();
  const uniqueCities = selectedProvince
    ? Array.from(new Set(regionFilteredMembers.filter(m => m.province === selectedProvince).map(m => (m as any).citta))).filter(Boolean).sort()
    : [];

  const filteredMembers = mapMembers
    .filter(m => selectedRegion ? m.region === selectedRegion : true)
    .filter(m => selectedProvince ? m.province === selectedProvince : true)
    .filter(m => selectedCity ? (m as any).citta?.trim() === selectedCity : true)
    .filter(m => searchName ? m.businessName.toLowerCase().includes(searchName.toLowerCase()) : true);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!authorized) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      {/* Guided Tour - Lista */}
      {!wizardOpen && (
        <Joyride
          key={`list-tour-${tourListKey}`}
          steps={listTourSteps}
          run={runTourList}
          continuous
          scrollToFirstStep
          onEvent={handleListTourEvent}
          locale={{ back: 'Indietro', close: 'Chiudi', last: 'Fine', next: 'Avanti', skip: 'Non mostrare più' }}
          options={{
            primaryColor: '#16a34a',
            zIndex: 10000,
            buttons: ['back', 'close', 'primary'],
            showProgress: true,
            scrollOffset: 80,
            textColor: '#374151',
            arrowColor: '#ffffff',
            backgroundColor: '#ffffff',
            width: 340
          }}
          styles={{
            tooltip: { borderRadius: 12, padding: 20, fontFamily: 'inherit', fontSize: 13 }
          } as any}
        />
      )}
      {/* Guided Tour - Mappa (Step 2) */}
      {wizardOpen && step === 2 && (
        <Joyride
          key={`map-tour-${tourMapKey}`}
          steps={mapTourSteps}
          run={runTourMap}
          continuous
          scrollToFirstStep
          onEvent={handleMapTourEvent}
          locale={{ back: 'Indietro', close: 'Chiudi', last: 'Fine', next: 'Avanti', skip: 'Non mostrare più' }}
          options={{
            primaryColor: '#16a34a',
            zIndex: 10000,
            buttons: ['back', 'close', 'primary'],
            showProgress: true,
            scrollOffset: 80,
            textColor: '#374151',
            arrowColor: '#ffffff',
            backgroundColor: '#ffffff',
            width: 340
          }}
          styles={{
            tooltip: { borderRadius: 12, padding: 20, fontFamily: 'inherit', fontSize: 13 }
          } as any}
        />
      )}
      {/* Guided Tour - Messaggio (Step 3) */}
      {wizardOpen && step === 3 && (
        <Joyride
          key={`msg-tour-${tourMsgKey}`}
          steps={msgTourSteps}
          run={runTourMsg}
          continuous
          scrollToFirstStep
          onEvent={handleMsgTourEvent}
          locale={{ back: 'Indietro', close: 'Chiudi', last: 'Fine', next: 'Avanti', skip: 'Non mostrare più' }}
          options={{
            primaryColor: '#16a34a',
            zIndex: 10000,
            buttons: ['back', 'close', 'primary'],
            showProgress: true,
            scrollOffset: 80,
            textColor: '#374151',
            arrowColor: '#ffffff',
            backgroundColor: '#ffffff',
            width: 340
          }}
          styles={{
            tooltip: { borderRadius: 12, padding: 20, fontFamily: 'inherit', fontSize: 13 }
          } as any}
        />
      )}

      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => wizardOpen ? resetWizard() : navigate('/area-riservata')} className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
              <Handshake className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              {wizardOpen ? 'Nuova Collaborazione' : 'Collabora con altri membri'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (wizardOpen && step === 3) {
                  setTourMsgKey(k => k + 1);
                  setRunTourMsg(true);
                } else if (wizardOpen && step === 2) {
                  setTourMapKey(k => k + 1);
                  setRunTourMap(true);
                } else {
                  setTourListKey(k => k + 1);
                  setRunTourList(true);
                }
              }}
              className="p-2 rounded-lg text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors"
              title="Guida"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
            {!wizardOpen && (
              <button id="tour-btn-nuova" onClick={() => setWizardOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors shadow-md">
                <Plus className="w-4 h-4" /> Nuova
              </button>
            )}
          </div>
        </div>

        {/* Wizard */}
        {wizardOpen ? (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
            {/* Stepper */}
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              {[
                { n: 1, label: 'Tipo pratica' },
                { n: 2, label: 'Trova collega' },
                { n: 3, label: 'Messaggio' },
                { n: 4, label: 'Conferma' }
              ].map(({ n, label }) => (
                <div key={n} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    step >= n ? 'bg-amber-500 text-white' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                  }`}>{n}</div>
                  <span className={`text-xs font-medium hidden sm:inline ${step >= n ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>{label}</span>
                  {n < 4 && <div className={`w-8 h-0.5 mx-1 ${step > n ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'}`} />}
                </div>
              ))}
            </div>

            <div className="p-6">
              {/* Step 1 - Tipo pratica */}
              {step === 1 && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Seleziona il tipo di pratica per cui hai bisogno di collaborazione:</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {PRACTICE_TYPES.map(practice => (
                      <button
                        key={practice}
                        onClick={() => handlePracticeSelect(practice)}
                        className="group flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-amber-400 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-all text-left"
                      >
                        <span className="text-2xl">{PRACTICE_ICONS[practice] || '📋'}</span>
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200 group-hover:text-amber-700 dark:group-hover:text-amber-400">{practice}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2 - Mappa */}
              {step === 2 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Trova un collega certificato per: <span className="font-semibold text-amber-600 dark:text-amber-400">{selectedPractice}</span>
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{filteredMembers.length} studi trovati</p>
                    </div>
                    <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1">
                      <ArrowLeft className="w-3 h-3" /> Cambia pratica
                    </button>
                  </div>

                  {/* Filtri */}
                  <div id="tour-map-filters" className="flex flex-wrap items-center gap-2 mb-4">
                    <div className="relative flex-1 min-w-[160px] max-w-[260px]">
                      <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        value={searchName}
                        onChange={e => setSearchName(e.target.value)}
                        placeholder="Cerca studio..."
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400"
                      />
                    </div>
                    <select
                      value={selectedRegion}
                      onChange={e => { setSelectedRegion(e.target.value); setSelectedProvince(''); setSelectedCity(''); }}
                      className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
                    >
                      <option value="">Tutte le regioni</option>
                      {uniqueRegions.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <select
                      value={selectedProvince}
                      onChange={e => { setSelectedProvince(e.target.value); setSelectedCity(''); }}
                      className="min-w-[160px] px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
                    >
                      <option value="">Tutte le province</option>
                      {uniqueProvinces.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    {selectedProvince && (
                      <select
                        value={selectedCity}
                        onChange={e => setSelectedCity(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
                      >
                        <option value="">Tutte le città</option>
                        {uniqueCities.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    )}
                    {(selectedRegion || selectedProvince || selectedCity || searchName) && (
                      <button
                        onClick={() => { setSelectedRegion(''); setSelectedProvince(''); setSelectedCity(''); setSearchName(''); }}
                        className="px-2.5 py-1.5 text-xs text-red-500 hover:text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors"
                      >
                        Azzera filtri
                      </button>
                    )}
                  </div>

                  {mapLoading ? (
                    <div className="flex justify-center py-16">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
                    </div>
                  ) : (
                    <>
                      {/* Mappa */}
                      <div id="tour-map-container" className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 mb-4 relative z-0">
                        <Suspense fallback={<div className="h-[400px] bg-gray-100 dark:bg-gray-800 flex items-center justify-center">Caricamento mappa...</div>}>
                          <MapWrapper
                            members={filteredMembers}
                            center={[41.9, 12.5]}
                            zoom={6}
                            settings={mapSettings}
                            fitToMarkers={filteredMembers.length > 0}
                            onMemberSelect={handleMemberSelect}
                          />
                        </Suspense>
                      </div>

                      {/* Lista sotto la mappa */}
                      {filteredMembers.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-4 py-2.5">
                            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                              Seleziona uno studio ({filteredMembers.length})
                            </p>
                            <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 animate-bounce">
                              <ChevronDown className="w-5 h-5" />
                              <span className="text-sm font-semibold">Scorri la lista</span>
                              <ChevronDown className="w-5 h-5" />
                            </div>
                          </div>
                        <div id="tour-map-list" className="max-h-60 overflow-y-auto space-y-2 border border-gray-200 dark:border-gray-700 rounded-xl p-2">
                          {filteredMembers.map(member => (
                            <button
                              key={member.id}
                              onClick={() => handleMemberSelect(member)}
                              className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-amber-400 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-all text-left"
                            >
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{member.businessName}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {(member as any).citta || ''} ({member.province})
                                </p>
                              </div>
                              <ArrowRight className="w-4 h-4 text-gray-400" />
                            </button>
                          ))}
                        </div>
                        </div>
                      )}

                      {filteredMembers.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Nessun collega trovato per questa pratica nella zona selezionata.</p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Step 3 - Messaggio */}
              {step === 3 && selectedMember && (
                <div>
                  <button onClick={() => setStep(2)} className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1 mb-4">
                    <ArrowLeft className="w-3 h-3" /> Cambia collega
                  </button>

                  {/* Info collega selezionato */}
                  <div id="tour-msg-info" className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-4">
                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium uppercase mb-2">Collega selezionato</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">{selectedMember.businessName}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" /> {(selectedMember as any).citta || ''} ({selectedMember.province})
                    </p>
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-200/50 dark:bg-amber-800/30 text-amber-700 dark:text-amber-400">
                        {PRACTICE_ICONS[selectedPractice] || '📋'} {selectedPractice}
                      </span>
                    </div>
                  </div>

                  {/* Textarea messaggio */}
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descrivi di cosa hai bisogno:
                  </label>
                  <textarea
                    id="tour-msg-textarea"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Scrivi il motivo della tua richiesta di collaborazione..."
                    rows={5}
                    maxLength={2000}
                    className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 text-right">{message.length}/2000</p>

                  <button
                    id="tour-msg-send"
                    onClick={handleSubmit}
                    disabled={!message.trim() || submitting}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white font-medium rounded-xl transition-colors shadow-md disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : (
                      <><Send className="w-4 h-4" /> Invia richiesta di collaborazione</>
                    )}
                  </button>
                </div>
              )}

              {/* Step 4 - Conferma */}
              {step === 4 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">Richiesta inviata!</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    La tua richiesta di collaborazione è stata inviata a <strong>{selectedMember?.businessName}</strong>.<br />
                    Riceverai una notifica quando il collega risponderà.
                  </p>
                  <div className="flex justify-center gap-3">
                    <button onClick={() => navigate(`/area-riservata/collabora/${createdId}`)} className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors">
                      Vedi dettaglio
                    </button>
                    <button onClick={resetWizard} className="px-5 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors">
                      Torna alla lista
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Lista collaborazioni */
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
            {/* Tabs */}
            <div id="tour-tabs" className="flex border-b border-gray-200 dark:border-gray-700">
              {(['received', 'sent'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    tab === t
                      ? 'text-amber-600 dark:text-amber-400 border-b-2 border-amber-500'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t === 'received' ? 'Ricevute' : 'Inviate'}
                </button>
              ))}
            </div>

            {/* Filtro status */}
            <div id="tour-filters" className="px-4 pt-3 flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              {['', 'new', 'in-progress', 'resolved'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                    statusFilter === s
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {s === '' ? 'Tutti' : STATUS_CONFIG[s]?.label || s}
                </button>
              ))}
            </div>

            {/* Lista */}
            <div id="tour-list" className="p-4">
              {listLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                </div>
              ) : collaborations.length === 0 ? (
                <div className="text-center py-12">
                  <Handshake className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {tab === 'received' ? 'Nessuna richiesta ricevuta' : 'Nessuna richiesta inviata'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {collaborations.map(c => {
                    const otherParty = tab === 'sent' ? c.targetSnapshot : c.requesterSnapshot;
                    const statusCfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.new;

                    return (
                      <button
                        key={c._id}
                        onClick={() => navigate(`/area-riservata/collabora/${c._id}`)}
                        className="w-full flex items-start gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-amber-300 dark:hover:border-amber-600 hover:bg-amber-50/50 dark:hover:bg-amber-900/5 transition-all text-left"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{otherParty?.nominativo || 'N/D'}</p>
                            {(c.unreadCount || 0) > 0 && (
                              <span className="min-w-[20px] h-5 bg-red-500 rounded-full flex items-center justify-center px-1.5">
                                <span className="text-xs text-white font-bold">{c.unreadCount}</span>
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mb-1">
                            <MapPin className="w-3 h-3" /> {otherParty?.citta || ''} {otherParty?.provincia ? `(${otherParty.provincia})` : ''}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-amber-600 dark:text-amber-400">{PRACTICE_ICONS[c.practiceType] || '📋'} {c.practiceType}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.color}`}>{statusCfg.label}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-gray-400 dark:text-gray-500">{new Date(c.updatedAt).toLocaleDateString('it-IT')}</p>
                          <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                            <MessageCircle className="w-3 h-3" /> {c.conversation?.length || 0}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Paginazione */}
              {pagination.pages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      onClick={() => loadCollaborations(p)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium ${
                        p === pagination.page ? 'bg-amber-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
